import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import Seo from '../components/Seo';
import CompanyField, { publicCompanySearch } from '../components/CompanyField';
import ContractSign, { emptySignature, signatureProblems } from '../components/ContractSign';
import { capturePartner, currentPartner } from '../config/partnerRef';
import { portalUrl } from '../config/site';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const PORTAL = portalUrl('/customer/login').startsWith('http') ? portalUrl('/customer/login') : 'https://portal.voip2.co.uk/customer/login';

/** Order a SIP Trunk online. The team checks every order before switching it on (SIP trunks attract fraud). */
const SipTrunkOrder = () => {
  const [f, setF] = useState({ first_name: '', last_name: '', company: '', company_number: null, email: '', phone: '', channels: 10, pbx: '', ip_addresses: '',
    numbers: '', notes: '', accept_terms: false, website: '' });
  const [errors, setErrors] = useState([]);
  const [signature, setSignature] = useState(emptySignature());
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [partner, setPartner] = useState(null);
  useEffect(() => {
    capturePartner();
    const code = currentPartner();
    if (code) fetch(`${BACKEND_URL}/api/trials/partner/${code}`).then((r) => (r.ok ? r.json() : null)).then(setPartner).catch(() => {});
  }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = [];
    if (!f.first_name.trim() || !f.last_name.trim()) errs.push('Enter your name.');
    if (!f.company.trim()) errs.push('Enter your company name.');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) errs.push('Enter a valid email address.');
    if (!/^[0-9 +()-]{6,30}$/.test(f.phone)) errs.push('Enter a contact phone number.');
    if (!(Number(f.channels) >= 1 && Number(f.channels) <= 500)) errs.push('Channels: 1 to 500.');
    if (!f.accept_terms) errs.push('Please accept the terms.');
    signatureProblems(signature).forEach((x) => errs.push(`Agreement: ${x}`));
    setErrors(errs);
    if (errs.length) return;
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/trials/sip-trunk`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...f, channels: Number(f.channels), partner: partner?.code || null, signature,
          pbx: f.pbx || null, ip_addresses: f.ip_addresses || null, numbers: f.numbers || null, notes: f.notes || null }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(Array.isArray(d.detail) ? d.detail.map((x) => (x.msg || '').replace(/^Value error, /, '')).join(' ') : d.detail || 'Something went wrong.');
      setDone(d);
    } catch (err) { setErrors([err.message]); } finally { setBusy(false); }
  };

  const field = (k, label, props = {}) => (
    <label className="block text-sm font-medium text-gray-700">{label}
      <input value={f[k]} onChange={set(k)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-[#0a1f2e] focus:outline-none" {...props} />
    </label>
  );

  return (
    <div className="min-h-screen bg-[#0a1f2e] px-4 pt-24 pb-16">
      <Seo
        title="Order a SIP Trunk Online"
        description="Order a VOIP2 SIP trunk online. Works with any PBX; we check and switch it on within one working day."
        path="/sip-trunk/order"
        breadcrumbs={[{ name: 'SIP Trunking', path: '/products/sip-trunking' }, { name: 'Order', path: '/sip-trunk/order' }]}
      />
      <div className="mx-auto max-w-5xl lg:flex lg:gap-0">
        <aside className="mb-6 rounded-xl bg-[#0f2a3d] p-6 text-white lg:mt-6 lg:mb-0 lg:w-80 lg:shrink-0 lg:rounded-r-none">
          <div className="font-display text-2xl font-semibold">VOIP<span className="text-[#c8ff00]">2</span></div>
          {partner && <p className="mt-3 inline-block rounded-full bg-white/10 px-3 py-1 text-xs text-white/80">Through our partner {partner.name}</p>}
          <p className="mt-6 text-lg"><b>SIP Trunk</b> for the phone system you already have.</p>
          <ul className="mt-5 space-y-2 text-sm text-white/80">
            {['Works with 3CX, FreePBX, Avaya, Mitel and more', 'Keep your numbers or get new ones', 'Pay as you go from your credit',
              'Change routing and IPs yourself in the portal'].map((t) => <li key={t} className="flex gap-2"><Check className="h-4 w-4 shrink-0 text-[#c8ff00]" />{t}</li>)}
          </ul>
          <p className="mt-6 text-xs text-white/50">We check every order and switch it on within one working day.</p>
        </aside>
        <main className="flex-1 rounded-xl bg-white p-6 shadow-2xl sm:p-10">
          {done ? (
            <div className="py-10 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#c8ff00]"><Check className="h-7 w-7 text-[#0a1f2e]" /></div>
              <h1 className="mt-6 text-2xl font-semibold text-[#0a1f2e]">Thanks for your order</h1>
              <p className="mx-auto mt-3 max-w-md text-gray-600">{done.message}</p>
              <a href={PORTAL} className="mt-6 inline-block rounded-full bg-[#0a1f2e] px-6 py-3 text-sm font-semibold text-white">Customer portal</a>
              <div><Link to="/products/sip-trunking" className="mt-4 inline-block text-sm text-[#0a1f2e] underline">Back to SIP Trunking</Link></div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <h1 className="text-2xl font-semibold text-[#0a1f2e]">Order a SIP Trunk</h1>
              {errors.length > 0 && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"><ul className="list-disc pl-5">{errors.map((x) => <li key={x}>{x}</li>)}</ul></div>}
              <div className="grid gap-4 sm:grid-cols-2">{field('first_name', 'First name')}{field('last_name', 'Last name')}</div>
              <CompanyField value={f.company} number={f.company_number} search={publicCompanySearch}
                onChange={(company, company_number) => setF((x) => ({ ...x, company, company_number }))} />
              <div className="grid gap-4 sm:grid-cols-2">{field('email', 'Work email', { type: 'email' })}{field('phone', 'Contact phone', { type: 'tel' })}</div>
              <div className="grid gap-4 sm:grid-cols-2">
                {field('channels', 'Channels (calls at the same time)', { type: 'number', min: 1, max: 500 })}
                {field('pbx', 'Your phone system (optional)', { placeholder: 'e.g. 3CX' })}
              </div>
              {field('ip_addresses', 'Public IP address(es) of your PBX (optional)', { placeholder: 'e.g. 81.2.69.160' })}
              {field('numbers', 'Numbers to bring, or new numbers you need (optional)')}
              <label className="block text-sm font-medium text-gray-700">Anything else (optional)
                <textarea value={f.notes} onChange={set('notes')} rows={3} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5" />
              </label>
              <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={set('website')} className="hidden" aria-hidden="true" />
              <ContractSign product="sip_trunk" company={f.company} companyNumber={f.company_number} value={signature} onChange={setSignature} />
              <label className="flex items-start gap-3 text-sm text-gray-600">
                <input type="checkbox" className="mt-1" checked={f.accept_terms} onChange={set('accept_terms')} />
                <span>I agree to the <Link to="/terms-of-service" className="underline" target="_blank">terms</Link> and <Link to="/privacy-policy" className="underline" target="_blank">privacy policy</Link>.</span>
              </label>
              <button type="submit" disabled={busy} className="rounded-full bg-[#c8ff00] px-6 py-3 text-sm font-semibold text-[#0a1f2e] disabled:opacity-50">
                {busy ? 'Sending…' : 'Place my order'}</button>
            </form>
          )}
        </main>
      </div>
    </div>
  );
};

export default SipTrunkOrder;
